import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Rating,
  Paper,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  IconButton,
} from '@mui/material';
import { ArrowBack, CloudUpload, Delete } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { Product, ReviewCreate } from '../types';
import { reviewsAPI, productsAPI } from '../services/api';

interface ReviewForm {
  user_name: string;
  user_email: string;
  rating: number;
  advantages: string;
  disadvantages: string;
  comment: string;
}

const AddReviewPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ReviewForm>({
    defaultValues: {
      user_name: '',
      user_email: '',
      rating: 0,
      advantages: '',
      disadvantages: '',
      comment: '',
    },
  });

  const rating = watch('rating');

  useEffect(() => {
    if (productId) {
      loadProduct();
    }
  }, [productId]);

  const loadProduct = async () => {
    if (!productId) return;

    try {
      setLoading(true);
      const products = await productsAPI.getAll();
      const product = products.find(p => p.id === productId);
      setProduct(product || null);
    } catch (err: any) {
      setError('Ошибка загрузки продукта');
      console.error('Error loading product:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files);
      const totalFiles = photos.length + newFiles.length;
      
      if (totalFiles > 10) {
        setError('Максимум 10 фотографий');
        return;
      }
      
      // Проверяем размер файлов (макс 5MB каждый)
      const maxSize = 5 * 1024 * 1024;
      const oversizedFiles = newFiles.filter(file => file.size > maxSize);
      
      if (oversizedFiles.length > 0) {
        setError('Размер файла не должен превышать 5MB');
        return;
      }
      
      setPhotos([...photos, ...newFiles]);
      setError('');
    }
  };

  const removePhoto = (index: number) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: ReviewForm) => {
    if (!productId) return;

    try {
      setSubmitting(true);
      setError('');

      const reviewData: ReviewCreate = {
        product_id: productId,
        user_name: data.user_name,
        user_email: data.user_email,
        rating: data.rating,
        advantages: data.advantages || undefined,
        disadvantages: data.disadvantages || undefined,
        comment: data.comment,
        photos: photos.length > 0 ? photos : undefined,
      };

      await reviewsAPI.create(reviewData);
      setSuccess('Отзыв успешно отправлен на модерацию!');
      
      // Перенаправляем через 2 секунды
      setTimeout(() => {
        navigate(`/products/${productId}/reviews`);
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Ошибка отправки отзыва');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">Продукт не найден</Alert>
          <Button onClick={() => navigate('/products')} sx={{ mt: 2 }}>
            Вернуться к продуктам
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(`/products/${productId}/reviews`)}
            sx={{ mr: 2 }}
          >
            Назад
          </Button>
          <Typography variant="h4" component="h1">
            Отзыв о "{product.name}"
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Paper sx={{ p: 4 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Основная информация
              </Typography>
              
              <Controller
                name="user_name"
                control={control}
                rules={{ required: 'Укажите ваше имя' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Ваше имя"
                    fullWidth
                    margin="normal"
                    error={!!errors.user_name}
                    helperText={errors.user_name?.message}
                  />
                )}
              />

              <Controller
                name="user_email"
                control={control}
                rules={{
                  required: 'Укажите ваш email',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Некорректный email адрес',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    type="email"
                    fullWidth
                    margin="normal"
                    error={!!errors.user_email}
                    helperText={errors.user_email?.message}
                  />
                )}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Оценка
              </Typography>
              <Controller
                name="rating"
                control={control}
                rules={{ min: { value: 1, message: 'Выберите оценку' } }}
                render={({ field }) => (
                  <Box>
                    <Rating
                      {...field}
                      size="large"
                      onChange={(_, value) => field.onChange(value || 0)}
                    />
                    {errors.rating && (
                      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        {errors.rating.message}
                      </Typography>
                    )}
                  </Box>
                )}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Детали отзыва
              </Typography>
              
              <Controller
                name="advantages"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Достоинства"
                    fullWidth
                    multiline
                    rows={3}
                    margin="normal"
                    placeholder="Что вам понравилось в продукте?"
                  />
                )}
              />

              <Controller
                name="disadvantages"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Недостатки"
                    fullWidth
                    multiline
                    rows={3}
                    margin="normal"
                    placeholder="Что можно улучшить?"
                  />
                )}
              />

              <Controller
                name="comment"
                control={control}
                rules={{ required: 'Напишите комментарий' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Комментарий"
                    fullWidth
                    multiline
                    rows={4}
                    margin="normal"
                    error={!!errors.comment}
                    helperText={errors.comment?.message}
                    placeholder="Поделитесь подробным мнением о продукте"
                  />
                )}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Фотографии (необязательно)
              </Typography>
              
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="photo-upload"
                multiple
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="photo-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  disabled={photos.length >= 10}
                >
                  Добавить фото ({photos.length}/10)
                </Button>
              </label>

              {photos.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {photos.map((photo, index) => (
                      <Chip
                        key={index}
                        label={photo.name}
                        onDelete={() => removePhoto(index)}
                        deleteIcon={<Delete />}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/products/${productId}/reviews`)}
              >
                Отмена
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting || rating === 0}
              >
                {submitting ? 'Отправка...' : 'Отправить отзыв'}
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default AddReviewPage;


